import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Avatar,
  CardMedia,
  InputBase,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Book as BookIcon,
  VideoLibrary as VideoIcon,
  Headphones as AudioIcon,
  Article as ArticleIcon,
  School as SchoolIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  GridView,
  SearchOutlined,
  FilterListOutlined,
  EditOutlined,
  DeleteOutline,
  Image,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import { List } from "lucide-react";
import apiService from "../../service/Api";
import CustomTextField from "../../components/common/Textfield";
import CustomSelect from "../../components/common/Select";
import Pagination from "../../components/common/Pagination";
import CourseViewDialog from "./courses-view/VideoView";
import ArticleViewDialog from "./courses-view/ArticleView";
import AudioPlayerDialog from "./courses-view/AudioPlayer";
import { IContentStats, ICourse } from "./types/courses-list";
import formatDate from "../../utils/FormatDate";
import { getEmbedUrl } from "./utils/GetEmbededUrl";
import { tokens } from "../../theme";

// Define response types for API calls
interface CourseResponse {
  results: ICourse[];
  count: number;
}

interface StatsResponse {
  data: {
    total_students: number;
    active_courses: number;
    articles_published: number;
    audio_content: number;
  };
  detail: string;
}

interface CourseCreateResponse {
  id: string;
  // Include other fields if needed, but id is sufficient for this use case
}

const StatCard = styled(motion.div)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-5px)",
    boxShadow: theme.shadows[10],
  },
}));

const CoursesList: React.FC = () => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  // State
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openCourseDialog, setOpenCourseDialog] = useState(false);
  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [openAudioDialog, setOpenAudioDialog] = useState(false);
  const [openArticleDialog, setOpenArticleDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [contentType, setContentType] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [stats, setStats] = useState<IContentStats>({
    totalStudents: 0,
    activeCourses: 0,
    articlesPublished: 0,
    audioContent: 0,
  });
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<ICourse | null>(null);
  const [editingCourse, setEditingCourse] = useState<ICourse | null>(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openArticleView, setOpenArticleView] = useState(false);
  const [openAudioPlayer, setOpenAudioPlayer] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  });


  const hasPermission = (permission: string) => {
    return (
      permission === "lms.change_course" || permission === "lms.delete_course"
    );
  };

  // Fetch courses from the backend
  const fetchCourses = React.useCallback(async () => {
    try {
      const response = await apiService.get<CourseResponse>(
        "/api/lms/courses/",
        {
          page: pagination.currentPage,
          page_size: pagination.pageSize,
          content_type: contentType !== "all" ? contentType : undefined,
          search: searchTerm || undefined,
        }
      );
      const { results, count } = response.data;
      setCourses(results);
      setPagination((prev) => ({ ...prev, totalCount: count }));
    } catch (error) {
      console.error("Failed to fetch courses:", error);
    }
  }, [contentType, pagination.currentPage, pagination.pageSize, searchTerm]);

  // Fetch stats from the backend
  const fetchStats = React.useCallback(async () => {
    try {
      const response = await apiService.get<StatsResponse>(
        "/api/lms/courses/stats/"
      );

      const statsData = response.data.data;
      setStats({
        totalStudents: statsData.total_students || 0,
        activeCourses: statsData.active_courses || 0,
        articlesPublished: statsData.articles_published || 0,
        audioContent: statsData.audio_content || 0,
      });
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  // Fetch courses and stats on component mount
  useEffect(() => {
    fetchCourses();
    fetchStats();
  }, [fetchCourses, fetchStats]);

  // Handle create course
  const handleCreateCourse = async (data: FormData) => {
    const rawUrl = data.get("youtube_url") as string;
    const cleanedUrl = getEmbedUrl(rawUrl);
    data.set("youtube_url", cleanedUrl);

    try {
      await apiService.post<CourseCreateResponse>("/api/lms/courses/", data);
      fetchCourses();
      handleCloseCourseDialog();
    } catch (error) {
      console.error("Failed to create course:", error);
    }
  };

  // Handle create video
  const handleCreateVideo = async (data: FormData) => {
    try {
      await apiService.post<CourseCreateResponse>("/api/lms/courses/", data);
      fetchCourses();
      handleCloseVideoDialog();
    } catch (error) {
      console.error("Failed to create video:", error);
    }
  };

  // Handle create audio
  const handleCreateAudio = async (data: FormData) => {
    try {
      await apiService.post<CourseCreateResponse>("/api/lms/courses/", data);
      fetchCourses();
      handleCloseAudioDialog();
    } catch (error) {
      console.error("Failed to create audio:", error);
    }
  };

  // Handle create article with additional images
  const handleCreateArticle = async (data: FormData) => {
    try {
      const additionalImages = data.getAll("additional_images") as File[];
      data.delete("additional_images"); // Remove additional_images from course data
      const courseResponse = await apiService.post<CourseCreateResponse>(
        "/api/lms/courses/",
        data
      );
      const courseId = courseResponse.data.id;

      // Upload additional images
      for (const image of additionalImages) {
        if (image) {
          const imageData = new FormData();
          imageData.append("course_id", courseId.toString());
          imageData.append("image", image);
          await apiService.post("/api/content-images/", imageData);
        }
      }
      fetchCourses();
      handleCloseArticleDialog();
    } catch (error) {
      console.error("Failed to create article:", error);
    }
  };

  const handleEditCourse = (course: ICourse) => {
    setEditingCourse(course);

    const type = course?.type?.toLowerCase?.() || "course"; // default fallback

    switch (type) {
      case "course":
        setOpenCourseDialog(true);
        break;
      case "video":
        setOpenVideoDialog(true);
        break;
      case "audio":
        setOpenAudioDialog(true);
        break;
      case "article":
        setOpenArticleDialog(true);
        break;
      default:
        setOpenCourseDialog(true);
    }
  };

  // Handle update course
  const handleUpdateCourse = async (data: FormData) => {
    const rawUrl = data.get("youtube_url") as string;
    const cleanedUrl = getEmbedUrl(rawUrl);
    data.set("youtube_url", cleanedUrl);
    try {
      await apiService.put<CourseCreateResponse>(
        `/api/lms/courses/${editingCourse!.id}/`,
        data
      );
      fetchCourses();
      handleCloseCourseDialog();
    } catch (error) {
      console.error("Failed to update course:", error);
    }
  };

  // Handle update article with additional images
  const handleUpdateArticle = async (data: FormData) => {
    try {
      const additionalImages = data.getAll("additional_images") as File[];
      data.delete("additional_images");
      await apiService.put<CourseCreateResponse>(
        `/api/lms/courses/${editingCourse!.id}/`,
        data
      );

      // Upload new additional images
      for (const image of additionalImages) {
        if (image) {
          const imageData = new FormData();
          imageData.append("course_id", editingCourse!.id.toString());
          imageData.append("image", image);
          await apiService.post("/api/content-images/", imageData);
        }
      }
      fetchCourses();
      handleCloseArticleDialog();
    } catch (error) {
      console.error("Failed to update article:", error);
    }
  };

  // Handle delete course
  const onDelete = async (id: string) => {
    try {
      await apiService.delete(`/api/lms/courses/${id}/`);
      fetchCourses();
    } catch (error) {
      console.error("Failed to delete course:", error);
    }
  };

  // Handle view course
  const handleViewCourse = (course: ICourse) => {
    setSelectedCourse(course);

    const type = course?.type?.toLowerCase?.() || "default";

    switch (type) {
      case "article":
        setOpenArticleView(true);
        break;
      case "audio":
        setOpenAudioPlayer(true);
        break;
      default:
        setOpenViewDialog(true);
    }
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedCourse(null);
  };

  const handleCloseArticleView = () => {
    setOpenArticleView(false);
    setSelectedCourse(null);
  };

  const handleCloseAudioPlayer = () => {
    setOpenAudioPlayer(false);
    setSelectedCourse(null);
  };

  const handleCloseCourseDialog = () => {
    setOpenCourseDialog(false);
    setEditingCourse(null);
  };

  const handleCloseVideoDialog = () => {
    setOpenVideoDialog(false);
    setEditingCourse(null);
  };

  const handleCloseAudioDialog = () => {
    setOpenAudioDialog(false);
    setEditingCourse(null);
  };

  const handleCloseArticleDialog = () => {
    setOpenArticleDialog(false);
    setEditingCourse(null);
  };

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleOpenCourseDialog = () => {
    setOpenCourseDialog(true);
    setOpenCreateDialog(false);
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePageSizeChange = (size: number) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handleOpenVideoDialog = () => {
    setOpenVideoDialog(true);
    setOpenCreateDialog(false);
  };

  const handleOpenAudioDialog = () => {
    setOpenAudioDialog(true);
    setOpenCreateDialog(false);
  };

  const handleOpenArticleDialog = () => {
    setOpenArticleDialog(true);
    setOpenCreateDialog(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleContentTypeChange = (event: SelectChangeEvent) => {
    setContentType(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Form submission handlers
  const handleCourseFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.append("content_type", "course");
    if (editingCourse) {
      handleUpdateCourse(formData);
    } else {
      handleCreateCourse(formData);
    }
  };

  const handleVideoFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.append("content_type", "video");
    if (editingCourse) {
      handleUpdateCourse(formData);
    } else {
      handleCreateVideo(formData);
    }
  };

  const handleAudioFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.append("content_type", "audio");
    if (editingCourse) {
      handleUpdateCourse(formData);
    } else {
      handleCreateAudio(formData);
    }
  };

  const handleArticleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.append("content_type", "article");
    if (editingCourse) {
      handleUpdateArticle(formData);
    } else {
      handleCreateArticle(formData);
    }
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              height: "100%",
              bgcolor: colors.primary[900],
              boxShadow: 0,
            }}
          >
            <StatCard transition={{ delay: 0.1 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "#bbdefb", color: "#1976d2", mr: 2 }}>
                  <SchoolIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {stats.totalStudents}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Students
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              height: "100%",
              bgcolor: colors.primary[900],
              boxShadow: 0,
            }}
          >
            <StatCard transition={{ delay: 0.2 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "#c8e6c9", color: "#388e3c", mr: 2 }}>
                  <BookIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {stats.activeCourses}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Courses
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              height: "100%",
              bgcolor: colors.primary[900],
              boxShadow: 0,
            }}
          >
            <StatCard transition={{ delay: 0.3 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "#ffcdd2", color: "#d32f2f", mr: 2 }}>
                  <ArticleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {stats.articlesPublished}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Articles Published
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper
            sx={{
              height: "100%",
              bgcolor: colors.primary[900],
              boxShadow: 0,
            }}
          >
            <StatCard transition={{ delay: 0.4 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar sx={{ bgcolor: "#fff9c4", color: "#fbc02d", mr: 2 }}>
                  <AudioIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {stats.audioContent}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Audio Content
                  </Typography>
                </Box>
              </Box>
            </StatCard>
          </Paper>
        </Grid>
      </Grid>
      <Box>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
            <Box
              sx={{ display: "flex", gap: 2, alignItems: "center", flex: 1 }}
            >
              <Box
                sx={{
                  backgroundColor: colors.primary[900],
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  pl: 1,
                  flex: 1,
                  maxWidth: 400,
                }}
              >
                <SearchOutlined sx={{ mr: 1, color: "white" }} />
                <InputBase
                  placeholder="Search by course name, course type..."
                  sx={{ ml: 1, flex: 1, color: colors.primary[700] }}
                  onChange={handleSearchChange}
                  value={searchTerm}
                />
                <IconButton>
                  <FilterListOutlined
                    sx={{ color: colors.primary[300] }}
                  />
                </IconButton>
              </Box>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <FormControl
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 120 }}
                >
                  <InputLabel id="content-type-label">Content Type</InputLabel>
                  <Select
                    labelId="content-type-label"
                    value={contentType}
                    onChange={handleContentTypeChange}
                    label="Content Type"
                    sx={{
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: `1px solid ${colors.primary[300]}`,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#614438",
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="video">Videos</MenuItem>
                    <MenuItem value="audio">Audio</MenuItem>
                    <MenuItem value="article">Articles</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  color={viewMode === "grid" ? "primary" : "default"}
                  onClick={() => setViewMode("grid")}
                  aria-label="grid view"
                >
                  <GridView />
                </IconButton>
                <IconButton
                  color={viewMode === "list" ? "primary" : "default"}
                  onClick={() => setViewMode("list")}
                  aria-label="list view"
                >
                  <List />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Add Content
              </Button>
            </Box>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {courses.map((course) => (
            <Grid
              key={course.id}
              size={{
                xs: 12,
                sm: viewMode === "grid" ? 6 : 12,
                md: viewMode === "grid" ? 3 : 12,
              }}
            >
              <Card
                sx={{
                  display: viewMode === "list" ? "flex" : "block",
                  height: "100%",
                  flexDirection: viewMode === "list" ? "row" : "column",
                  alignItems: viewMode === "list" ? "center" : "stretch",
                  boxShadow: 0,
                  "&:hover": {
                    boxShadow: 3,
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    "&:hover .card-actions": {
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                    width: viewMode === "list" ? 150 : "100%",
                    flexShrink: 0,
                  }}
                >
                  {course.image ? (
                    <CardMedia
                      component="img"
                      sx={{
                        width: "100%",
                        height: viewMode === "list" ? 150 : 200,
                        objectFit: "cover",
                      }}
                      image={course.image}
                      loading="lazy"
                      alt={course.title}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: viewMode === "list" ? 150 : 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: colors.primary[800],
                        borderRadius: 1,
                      }}
                    >
                      <Box textAlign="center">
                        <Image sx={{ fontSize: 40, color: "#9e9e9e" }} />
                      </Box>
                    </Box>
                  )}
                  {(hasPermission("lms.change_course") ||
                    hasPermission("lms.delete_course")) && (
                    <Box
                      className="card-actions"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        display: "flex",
                        gap: 1,
                        opacity: 0,
                        pointerEvents: "none",
                        transition: "opacity 0.3s ease",
                        zIndex: 1,
                      }}
                    >
                      {hasPermission("lms.change_course") && (
                        <IconButton
                          aria-label="edit"
                          size="small"
                          onClick={() => handleEditCourse(course)}
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            color:
                              colors.primary[400],
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                          }}
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      )}
                      {hasPermission("lms.delete_course") && (
                        <IconButton
                          aria-label="delete"
                          size="small"
                          onClick={() => onDelete(course.id.toString())}
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            color: "#EB4335",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 1)",
                            },
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
                <CardContent
                  onClick={() => handleViewCourse(course)}
                  sx={{ cursor: "pointer", flexGrow: 1 }}
                >
                  {/* <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Chip
                      label={course.type}
                      size="small"
                      sx={{
                        bgcolor: colors.primary[800],
                        fontWeight: "bold",
                      }}
                    />
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AccessTime
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {course.readTime}
                      </Typography>
                    </Box>
                  </Box> */}
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    sx={{ fontWeight: "bold" }}
                  >
                    {course.title}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {course.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      Date Published: {formatDate(course.created_at)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Create Content Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCloseCreateDialog}
        fullWidth
        component={motion.div}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Create new content</Typography>
            <IconButton onClick={handleCloseCreateDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    boxShadow: 0,
                    border: `1px solid ${colors.primary[300]}`,
                    "&:hover": { boxShadow: 6 },
                  }}
                  onClick={handleOpenCourseDialog}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <BookIcon fontSize="large" color="primary" />
                      <Typography variant="h6" ml={1}>
                        Course
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Create and publish educational content for learners.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    boxShadow: 0,
                    border: `1px solid ${colors.primary[300]}`,
                    "&:hover": { boxShadow: 6 },
                  }}
                  onClick={handleOpenVideoDialog}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <VideoIcon fontSize="large" color="primary" />
                      <Typography variant="h6" ml={1}>
                        Video
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Upload video content for courses or as standalone
                      material.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    boxShadow: 0,
                    border: `1px solid ${colors.primary[300]}`,
                    "&:hover": { boxShadow: 6 },
                  }}
                  onClick={handleOpenArticleDialog}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ArticleIcon fontSize="large" color="primary" />
                      <Typography variant="h6" ml={1}>
                        Article
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Create standalone articles containing educational content.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    boxShadow: 0,
                    border: `1px solid ${colors.primary[300]}`,
                    "&:hover": { boxShadow: 6 },
                  }}
                  onClick={handleOpenAudioDialog}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <AudioIcon fontSize="large" color="primary" />
                      <Typography variant="h6" ml={1}>
                        Audio
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Upload audio books or lesson recordings with thumbnails.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCreateDialog}
            sx={{ backgroundColor: colors.primary[800] }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Course Dialog */}
      <Dialog
        open={openCourseDialog}
        onClose={handleCloseCourseDialog}
        fullWidth
        component={motion.div}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editingCourse ? "Edit Course" : "Create New Course"}
            </Typography>
            <IconButton onClick={handleCloseCourseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleCourseFormSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Course Title"
                  name="title"
                  fullWidth
                  required
                  variant="outlined"
                  placeholder="Enter course title"
                  defaultValue={editingCourse?.title || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Description"
                  name="description"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Enter course description"
                  defaultValue={editingCourse?.description || ""}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Course Thumbnail
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: colors.primary[800],
                    position: "relative",
                  }}
                  component={motion.div}
                  whileHover={{ backgroundColor: "#f0f0f0" }}
                >
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="thumbnail-upload"
                    type="file"
                    name="image"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        const file = e.target.files[0];
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setThumbnailPreview(ev.target?.result as string); // store in state
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <label htmlFor="thumbnail-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Thumbnail
                    </Button>
                  </label>

                  <Box sx={{ mt: 2 }}>
                    <img
                      src={thumbnailPreview || editingCourse?.image || ""}
                      alt="Course Thumbnail"
                      style={{
                        maxWidth: "100%",
                        maxHeight: 200,
                        borderRadius: 4,
                        objectFit: "cover",
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Recommended size: 1280x720px (16:9 ratio)
                  </Typography>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Course Content Source
                </Typography>
                <Tabs value={0} aria-label="content source tabs">
                  <Tab label="YouTube URL" />
                  <Tab label="Upload Video File" />
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  <CustomTextField
                    label="YouTube URL"
                    name="youtube_url"
                    fullWidth
                    variant="outlined"
                    placeholder="https://www.youtube.com/watch?v=..."
                    defaultValue={editingCourse?.youtube_url || ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    label="Publication Status"
                    name="status"
                    defaultValue={editingCourse?.status || "draft"}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                  </CustomSelect>
                </FormControl>
              </Grid>
            </Grid>
            <DialogActions>
              <Button
                onClick={handleCloseCourseDialog}
                sx={{ backgroundColor: colors.primary[800] }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editingCourse ? "Update Course" : "Create Course"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Video Dialog */}
      <Dialog
        open={openVideoDialog}
        onClose={handleCloseVideoDialog}
        fullWidth
        component={motion.div}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editingCourse ? "Edit Video" : "Add Video to Course"}
            </Typography>
            <IconButton onClick={handleCloseVideoDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleVideoFormSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth required>
                  <CustomSelect
                    labelId="course-select-label"
                    label="Select Course"
                    name="parent_id"
                    defaultValue={editingCourse?.parent_id || ""}
                  >
                    <MenuItem value="">
                      <em>Select a course</em>
                    </MenuItem>
                    {courses.map((course) => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.title}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Video Title"
                  name="title"
                  fullWidth
                  required
                  variant="outlined"
                  placeholder="Enter video title"
                  defaultValue={editingCourse?.title || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Description"
                  name="description"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter video description"
                  defaultValue={editingCourse?.description || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Video Source
                </Typography>
                <Tabs value={0} aria-label="video source tabs">
                  <Tab label="YouTube URL" />
                  <Tab label="Upload Video File" />
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  <CustomTextField
                    label="YouTube URL"
                    name="youtube_url"
                    fullWidth
                    variant="outlined"
                    placeholder="https://www.youtube.com/watch?v=..."
                    defaultValue={editingCourse?.youtube_url || ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <input
                    accept="video/*"
                    style={{ display: "none" }}
                    id="video-file-upload"
                    type="file"
                    name="video_file"
                  />
                  <label htmlFor="video-file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Video File
                    </Button>
                  </label>
                </Box>
              </Grid>
            </Grid>
            <DialogActions>
              <Button
                onClick={handleCloseVideoDialog}
                sx={{ backgroundColor: colors.primary[800] }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editingCourse ? "Update Video" : "Add Video"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Audio Dialog */}
      <Dialog
        open={openAudioDialog}
        onClose={handleCloseAudioDialog}
        fullWidth
        component={motion.div}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editingCourse ? "Edit Audio" : "Add Audio Content"}
            </Typography>
            <IconButton onClick={handleCloseAudioDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleAudioFormSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    labelId="audio-type-label"
                    label="Audio Type"
                    name="audio_type"
                    defaultValue={editingCourse?.audio_type || "lesson"}
                  >
                    <MenuItem value="lesson">Lesson Audio</MenuItem>
                    <MenuItem value="audiobook">Audiobook</MenuItem>
                    <MenuItem value="podcast">Podcast</MenuItem>
                  </CustomSelect>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    labelId="course-audio-select-label"
                    label="Related Course (Optional)"
                    name="parent_id"
                    defaultValue={editingCourse?.parent_id || ""}
                  >
                    <MenuItem value="">
                      <em>None (Standalone Audio)</em>
                    </MenuItem>
                    {courses
                      .filter((course) => course.type === "course")
                      .map((course) => (
                        <MenuItem key={course.id} value={course.id}>
                          {course.title}
                        </MenuItem>
                      ))}
                  </CustomSelect>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Audio Title"
                  name="title"
                  fullWidth
                  required
                  variant="outlined"
                  placeholder="Enter audio title"
                  defaultValue={editingCourse?.title || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Description"
                  name="description"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter audio description"
                  defaultValue={editingCourse?.description || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Audio File
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: colors.primary[800],
                  }}
                  component={motion.div}
                  whileHover={{ backgroundColor: "#f0f0f0" }}
                >
                  <input
                    accept="audio/*"
                    style={{ display: "none" }}
                    id="audio-file-upload"
                    type="file"
                    name="audio_file"
                  />
                  <label htmlFor="audio-file-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Audio File
                    </Button>
                  </label>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Supported formats: MP3, WAV, AAC (max 500MB)
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Audio Thumbnail
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: colors.primary[800],
                  }}
                  component={motion.div}
                  whileHover={{ backgroundColor: "#f0f0f0" }}
                >
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="audio-thumbnail-upload"
                    type="file"
                    name="image"
                  />
                  <label htmlFor="audio-thumbnail-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Thumbnail
                    </Button>
                  </label>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Recommended size: 500x500px (square ratio for audio)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <DialogActions>
              <Button
                onClick={handleCloseAudioDialog}
                sx={{ backgroundColor: colors.primary[800] }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editingCourse ? "Update Audio" : "Add Audio"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog
        open={openArticleDialog}
        onClose={handleCloseArticleDialog}
        fullWidth
        component={motion.div}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {editingCourse ? "Edit Article" : "Create Article/Blog Post"}
            </Typography>
            <IconButton onClick={handleCloseArticleDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleArticleFormSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Article Title"
                  name="title"
                  fullWidth
                  required
                  variant="outlined"
                  placeholder="Enter article title"
                  defaultValue={editingCourse?.title || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    labelId="article-category-label"
                    label="Category"
                    name="category"
                    defaultValue={editingCourse?.category || "tutorial"}
                  >
                    <MenuItem value="tutorial">Tutorial</MenuItem>
                    <MenuItem value="guide">Guide</MenuItem>
                    <MenuItem value="case-study">Case Study</MenuItem>
                    <MenuItem value="theory">Theoretical Concept</MenuItem>
                  </CustomSelect>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Featured Image
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: colors.primary[800],
                  }}
                  component={motion.div}
                  whileHover={{ backgroundColor: "#f0f0f0" }}
                >
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="article-image-upload"
                    type="file"
                    name="image"
                  />
                  <label htmlFor="article-image-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                    >
                      Upload Featured Image
                    </Button>
                  </label>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Recommended size: 1200x630px
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <CustomTextField
                  label="Article Content"
                  name="content_text"
                  fullWidth
                  multiline
                  rows={10}
                  variant="outlined"
                  placeholder="Write your article content here..."
                  defaultValue={editingCourse?.content_text || ""}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Additional Images
                </Typography>
                <Box
                  sx={{
                    border: `2px dashed ${colors.primary[300]}`,
                    borderRadius: 1,
                    p: 3,
                    textAlign: "center",
                    bgcolor: colors.primary[800],
                  }}
                  component={motion.div}
                  whileHover={{ backgroundColor: "#f0f0f0" }}
                >
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="additional-images-upload"
                    multiple
                    type="file"
                    name="additional_images"
                  />
                  <label htmlFor="additional-images-upload">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<ImageIcon />}
                    >
                      Upload Additional Images
                    </Button>
                  </label>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    You can select multiple images (max 5)
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <CustomSelect
                    labelId="article-status-label"
                    label="Publication Status"
                    name="status"
                    defaultValue={editingCourse?.status || "draft"}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                  </CustomSelect>
                </FormControl>
              </Grid>
            </Grid>
            <DialogActions>
              <Button
                onClick={handleCloseArticleDialog}
                sx={{ backgroundColor: colors.primary[800] }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {editingCourse ? "Update Article" : "Create Article"}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Pagination
        currentPage={pagination.currentPage}
        pageSize={pagination.pageSize}
        totalCount={pagination.totalCount}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
      <CourseViewDialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        course={selectedCourse}
      />
      <ArticleViewDialog
        open={openArticleView}
        onClose={handleCloseArticleView}
        article={selectedCourse}
      />
      <AudioPlayerDialog
        open={openAudioPlayer}
        onClose={handleCloseAudioPlayer}
        audio={selectedCourse}
      />
    </Box>
  );
};

export default CoursesList;
